import { useState, useEffect, useRef, useCallback } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Cell {
  symbol: string;
  color: string;
}

interface DiffMode {
  id: string;
  label: string;
  desc: string;
  xp: number;
}

interface Rank {
  min: number;
  id: string;
  label: string;
  color: string;
  bg: string;
}

interface BestRecord {
  score?: number;
  level?: number;
  accuracy?: number;
  streak?: number;
  xp?: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SYMBOLS: string[] = ["▲", "■", "◆", "★", "⬡", "◎", "✖", "▼", "☀", "♦", "❋", "◉"];
const COLORS: string[] = [
  "text-blue-600", "text-red-500", "text-amber-500",
  "text-green-600", "text-purple-600", "text-rose-500",
  "text-teal-600", "text-orange-500",
];

const DIFF_MODES: DiffMode[] = [
  { id: "easy",   label: "Easy",   desc: "Symbol change only",  xp: 100  },
  { id: "medium", label: "Medium", desc: "Color change only",   xp: 250  },
  { id: "hard",   label: "Hard",   desc: "Position swap",       xp: 500  },
  { id: "expert", label: "Expert", desc: "Multiple changes",    xp: 1000 },
];

const RANKS: Rank[] = [
  { min: 0,    id: "D", label: "Beginner",         color: "text-gray-500",   bg: "bg-gray-100"  },
  { min: 200,  id: "C", label: "Active Learner",    color: "text-blue-600",   bg: "bg-blue-50"   },
  { min: 500,  id: "B", label: "Skilled Observer",  color: "text-green-600",  bg: "bg-green-50"  },
  { min: 1200, id: "A", label: "Advanced Observer", color: "text-purple-600", bg: "bg-purple-50" },
  { min: 2500, id: "S", label: "Master Observer",   color: "text-amber-600",  bg: "bg-amber-50"  },
];

const XP_PER_LEVEL = 500;
const MAX_LIVES = 3;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getRank(xp: number): Rank {
  let r = RANKS[0];
  for (const rank of RANKS) if (xp >= rank.min) r = rank;
  return r;
}

function getGridSize(level: number): number {
  if (level >= 30) return 7;
  if (level >= 20) return 6;
  if (level >= 10) return 5;
  return 4;
}

function getMemorizeTime(level: number): number {
  if (level >= 30) return 1000;
  if (level >= 20) return 2000;
  if (level >= 10) return 3000;
  if (level >= 5)  return 4000;
  return 5000;
}

function randomFrom<T>(arr: T[], exclude?: T): T {
  const pool = exclude !== undefined ? arr.filter(x => x !== exclude) : arr;
  return pool[Math.floor(Math.random() * pool.length)];
}

function randomCell(): Cell {
  return {
    symbol: randomFrom(SYMBOLS),
    color:  randomFrom(COLORS),
  };
}

function loadBest(): BestRecord {
  try {
    return JSON.parse(localStorage.getItem("bharatpariksha_obs") || "{}");
  } catch { return {}; }
}

function saveBest(data: BestRecord): void {
  try { localStorage.setItem("bharatpariksha_obs", JSON.stringify(data)); } catch {}
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Heart({ filled }: { filled: boolean }) {
  return (
    <span className={`text-xl ${filled ? "text-red-500" : "text-gray-300"}`}>♥</span>
  );
}

function StatCard({
  label, value, sub, highlight,
}: {
  label: string; value: string | number; sub?: string; highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl p-3 border ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"} shadow-sm text-center`}>
      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-xl font-bold ${highlight ? "text-amber-700" : "text-gray-800"}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function XPBar({ xp }: { xp: number }) {
  const lvl = Math.floor(xp / XP_PER_LEVEL) + 1;
  const xpInLevel = xp % XP_PER_LEVEL;
  const pct = Math.round((xpInLevel / XP_PER_LEVEL) * 100);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-xs font-semibold text-gray-600">Level {lvl}</span>
        <span className="text-xs text-gray-400">{xpInLevel} / {XP_PER_LEVEL} XP</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Timer bar: starts full-width, shrinks to 0 over `ms` milliseconds ──
function TimerBar({ ms, active }: { ms: number; active: boolean }) {
  const [width, setWidth] = useState(100);
  const [seconds, setSeconds] = useState(Math.ceil(ms / 1000));
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      setWidth(100);
      setSeconds(Math.ceil(ms / 1000));
      return;
    }

    // Reset then kick off on next frame so CSS sees 100% first
    setWidth(100);
    setSeconds(Math.ceil(ms / 1000));

    const startAnim = (timestamp: number) => {
      startRef.current = timestamp;

      const tick = (now: number) => {
        const elapsed = now - (startRef.current ?? now);
        const remaining = Math.max(0, ms - elapsed);
        const pct = (remaining / ms) * 100;
        setWidth(pct);
        setSeconds(Math.ceil(remaining / 1000));

        if (remaining > 0) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    };

    // Tiny delay so the browser paints 100% before we start shrinking
    const raf = requestAnimationFrame(startAnim);

    return () => {
      cancelAnimationFrame(raf);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, ms]);

  return (
    <div className="mb-4">
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span className="font-medium text-amber-600">⏱ Memorise the grid!</span>
        <span className="font-bold text-amber-700 tabular-nums">{active ? `${seconds}s` : "—"}</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600"
          style={{ width: `${width}%`, transition: "none" }}
        />
      </div>
    </div>
  );
}

function DifficultyBadge({ mode }: { mode: string }) {
  const colors: Record<string, string> = {
    easy:   "bg-green-100 text-green-700 border-green-200",
    medium: "bg-blue-100  text-blue-700  border-blue-200",
    hard:   "bg-purple-100 text-purple-700 border-purple-200",
    expert: "bg-red-100   text-red-700   border-red-200",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${colors[mode]}`}>
      {DIFF_MODES.find(d => d.id === mode)?.label}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type GameStatus = "idle" | "memorize" | "find" | "result" | "gameover";
type ResultType = "correct" | "wrong" | null;

export default function ObservationGame() {
  // Core game state
  const [status, setStatus]               = useState<GameStatus>("idle");
  const [grid, setGrid]                   = useState<Cell[]>([]);
  const [gridSize, setGridSize]           = useState<number>(4);
  const [targetCells, setTargetCells]     = useState<number[]>([]);
  const [originalCells, setOriginalCells] = useState<Record<number, Cell>>({});
  const [result, setResult]               = useState<ResultType>(null);
  const [flashIdx, setFlashIdx]           = useState<number | null>(null);
  const [flashType, setFlashType]         = useState<"correct" | "wrong" | null>(null);

  // Progress
  const [score, setScore]             = useState(0);
  const [attempts, setAttempts]       = useState(0);
  const [level, setLevel]             = useState(1);
  const [xp, setXp]                   = useState(0);
  const [lives, setLives]             = useState(MAX_LIVES);
  const [streak, setStreak]           = useState(0);
  const [bestStreak, setBestStreak]   = useState(0);

  // UI extras
  const [diffMode, setDiffMode]           = useState("easy");
  const [levelUpAnim, setLevelUpAnim]     = useState(false);
  const [streakBonus, setStreakBonus]     = useState<string | null>(null);
  const [timerActive, setTimerActive]     = useState(false);

  // Best records (localStorage)
  const [best, setBest] = useState<BestRecord>(() => loadBest());

  const roundTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived
  const xpLevel    = Math.floor(xp / XP_PER_LEVEL) + 1;
  const accuracy   = attempts > 0 ? Math.round((score / attempts) * 100) : 0;
  const rank       = getRank(xp);
  const memMs      = getMemorizeTime(level);
  const diffConfig = DIFF_MODES.find(d => d.id === diffMode)!;

  const isMemorize = status === "memorize";
  const isFind     = status === "find";

  // Cleanup on unmount
  useEffect(() => () => {
    if (roundTimerRef.current) clearTimeout(roundTimerRef.current);
  }, []);

  // ── Build changed grid ──
  const applyChanges = useCallback((
    baseGrid: Cell[],
    size: number,
    mode: string,
    lv: number
  ) => {
    const total = size * size;
    const copy = baseGrid.map(c => ({ ...c }));
    const originals: Record<number, Cell> = {};
    const changed: number[] = [];

    const numChanges = (mode === "expert" && lv >= 20) ? 3
                     : (mode === "expert" && lv >= 10) ? 2
                     : 1;

    const pickUnique = (): number => {
      let idx: number;
      do { idx = Math.floor(Math.random() * total); } while (changed.includes(idx));
      return idx;
    };

    for (let i = 0; i < numChanges; i++) {
      const idx = pickUnique();
      changed.push(idx);
      originals[idx] = { ...baseGrid[idx] };

      if (mode === "easy") {
        copy[idx] = { ...copy[idx], symbol: randomFrom(SYMBOLS, copy[idx].symbol) };
      } else if (mode === "medium") {
        copy[idx] = { ...copy[idx], color: randomFrom(COLORS, copy[idx].color) };
      } else if (mode === "hard") {
        const other = pickUnique();
        originals[other] = { ...baseGrid[other] };
        changed.push(other);
        [copy[idx], copy[other]] = [copy[other], copy[idx]];
      } else if (mode === "expert") {
        copy[idx] = {
          symbol: randomFrom(SYMBOLS, copy[idx].symbol),
          color:  randomFrom(COLORS,  copy[idx].color),
        };
        if (lv >= 20) {
          const other = pickUnique();
          originals[other] = { ...baseGrid[other] };
          changed.push(other);
          [copy[idx], copy[other]] = [copy[other], copy[idx]];
        }
      }
    }

    return { changedGrid: copy, targetIndices: changed, originals };
  }, []);

  // ── Start round ──
  const startRound = useCallback(() => {
    if (roundTimerRef.current) clearTimeout(roundTimerRef.current);

    const size = getGridSize(level);
    const total = size * size;
    const base: Cell[] = Array.from({ length: total }, randomCell);
    const ms = getMemorizeTime(level);

    setGrid(base);
    setGridSize(size);
    setResult(null);
    setFlashIdx(null);
    setFlashType(null);
    setStatus("memorize");
    setTimerActive(true);

    roundTimerRef.current = setTimeout(() => {
      const { changedGrid, targetIndices, originals } = applyChanges(base, size, diffMode, level);
      setGrid(changedGrid);
      setTargetCells(targetIndices);
      setOriginalCells(originals);
      setTimerActive(false);
      setStatus("find");
    }, ms);
  }, [level, diffMode, applyChanges]);

  // ── Handle cell click ──
  const handleCellClick = useCallback((idx: number) => {
    if (status !== "find") return;

    const isCorrect = targetCells.includes(idx);
    setAttempts(a => a + 1);
    setFlashIdx(idx);
    setFlashType(isCorrect ? "correct" : "wrong");

    if (isCorrect) {
      const baseXP = diffConfig.xp;
      let bonus = 0;
      const newStreak = streak + 1;
      setStreak(newStreak);

      if      (newStreak >= 10) { bonus = baseXP;                        setStreakBonus(`🔥 10-Streak! +${bonus} Bonus XP`); }
      else if (newStreak >= 5)  { bonus = Math.floor(baseXP * 0.5);      setStreakBonus(`⚡ 5-Streak! +${bonus} Bonus XP`);  }
      else if (newStreak >= 3)  { bonus = Math.floor(baseXP * 0.25);     setStreakBonus(`✨ 3-Streak! +${bonus} Bonus XP`);  }
      else                      { setStreakBonus(null); }

      const totalXP = baseXP + bonus;
      const newXP   = xp + totalXP;

      if (newStreak > bestStreak) setBestStreak(newStreak);

      if (Math.floor(newXP / XP_PER_LEVEL) > Math.floor(xp / XP_PER_LEVEL)) {
        setLevelUpAnim(true);
        setTimeout(() => setLevelUpAnim(false), 1800);
      }

      setXp(newXP);
      setScore(s => s + 1);
      setLevel(l => l + 1);
      setResult("correct");
    } else {
      setStreak(0);
      setStreakBonus(null);
      const newLives = lives - 1;
      setLives(newLives);
      setResult("wrong");

      if (newLives <= 0) {
        setTimeout(() => {
          const newBest: BestRecord = {
            score:    Math.max(best.score    ?? 0, score),
            level:    Math.max(best.level    ?? 0, level),
            accuracy: Math.max(best.accuracy ?? 0, accuracy),
            streak:   Math.max(best.streak   ?? 0, bestStreak),
            xp:       Math.max(best.xp       ?? 0, xp),
          };
          setBest(newBest);
          saveBest(newBest);
          setStatus("gameover");
        }, 700);
        return;
      }
    }

    setTimeout(() => {
      setFlashIdx(null);
      setFlashType(null);
      setStatus("result");
    }, 600);
  }, [status, targetCells, streak, bestStreak, xp, level, lives, score, accuracy, best, diffConfig]);

  // ── Full reset ──
  const resetGame = useCallback(() => {
    if (roundTimerRef.current) clearTimeout(roundTimerRef.current);
    setScore(0); setAttempts(0); setLevel(1); setXp(0);
    setLives(MAX_LIVES); setStreak(0); setBestStreak(0);
    setGrid([]); setTargetCells([]); setOriginalCells({});
    setResult(null); setFlashIdx(null); setFlashType(null);
    setStatus("idle"); setStreakBonus(null); setTimerActive(false);
  }, []);

  const cols = gridSize;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#FAF6EF] py-6 px-4 font-sans">

      {/* Level-up overlay */}
      {levelUpAnim && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <div className="bg-amber-400 text-white text-3xl font-black px-10 py-5 rounded-2xl shadow-2xl animate-bounce">
            🎉 Level Up! → Level {xpLevel}
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto space-y-4">

        {/* ── Header ── */}
        <div className="bg-white rounded-2xl border border-amber-100 shadow-sm px-5 py-4 flex justify-between items-center">
          <div>
            <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-0.5">
              BharatPariksha · Learning Tools
            </p>
            <h1 className="text-2xl font-extrabold text-gray-800">
              Observation <span className="text-amber-600">Challenge</span>
            </h1>
          </div>
          <div className={`rounded-xl px-3 py-2 text-center ${rank.bg}`}>
            <p className={`text-2xl font-black ${rank.color}`}>{rank.id}</p>
            <p className="text-xs text-gray-500 font-medium">{rank.label}</p>
          </div>
        </div>

        {/* ── XP Bar ── */}
        <XPBar xp={xp} />

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-4 gap-2">
          <StatCard label="Score"    value={score} />
          <StatCard label="Accuracy" value={`${accuracy}%`} highlight={accuracy >= 80} />
          <StatCard label="Streak"   value={streak} sub={`Best: ${bestStreak}`} />
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 text-center">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Lives</p>
            <div className="flex justify-center gap-0.5">
              {Array.from({ length: MAX_LIVES }).map((_, i) => (
                <Heart key={i} filled={i < lives} />
              ))}
            </div>
          </div>
        </div>

        {/* ── Difficulty Selector ── */}
        {(status === "idle" || status === "result") && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Difficulty Mode</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DIFF_MODES.map(d => (
                <button
                  key={d.id}
                  onClick={() => setDiffMode(d.id)}
                  className={`rounded-xl p-2.5 border text-left transition-all ${
                    diffMode === d.id
                      ? "bg-amber-500 border-amber-500 text-white"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-amber-300"
                  }`}
                >
                  <p className="font-bold text-sm">{d.label}</p>
                  <p className={`text-xs mt-0.5 ${diffMode === d.id ? "text-amber-100" : "text-gray-400"}`}>{d.desc}</p>
                  <p className={`text-xs font-semibold mt-1 ${diffMode === d.id ? "text-white" : "text-amber-600"}`}>+{d.xp} XP</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Game Over Screen ── */}
        {status === "gameover" && (
          <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-6 text-center">
            <p className="text-4xl mb-2">📊</p>
            <h2 className="text-2xl font-black text-gray-800 mb-1">Session Complete</h2>
            <p className="text-sm text-gray-400 mb-5">Here's how you performed this round</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              <StatCard label="Final Score"   value={score} />
              <StatCard label="Level Reached" value={`Lv. ${level}`} />
              <StatCard label="XP Earned"     value={`${xp} XP`} highlight />
              <StatCard label="Accuracy"      value={`${accuracy}%`} />
              <StatCard label="Best Streak"   value={bestStreak} />
              <div className={`rounded-xl p-3 border ${rank.bg} text-center`}>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Final Rank</p>
                <p className={`text-xl font-black ${rank.color}`}>{rank.id}</p>
                <p className="text-xs text-gray-500">{rank.label}</p>
              </div>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 mb-4 text-left">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Personal Bests</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                <span>🏆 Best Score: <b>{best.score ?? 0}</b></span>
                <span>🎯 Best Accuracy: <b>{best.accuracy ?? 0}%</b></span>
                <span>⚡ Best Streak: <b>{best.streak ?? 0}</b></span>
                <span>🌟 Best XP: <b>{best.xp ?? 0}</b></span>
              </div>
            </div>
            <button
              onClick={resetGame}
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        )}

        {/* ── Main Game Card ── */}
        {status !== "gameover" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">

            {/* Status message */}
            <div className={`rounded-xl px-4 py-2.5 mb-4 text-sm font-medium text-center transition-all ${
              isMemorize              ? "bg-amber-50  text-amber-700  border border-amber-200"  :
              isFind                  ? "bg-blue-50   text-blue-700   border border-blue-200"   :
              result === "correct"    ? "bg-green-50  text-green-700  border border-green-200"  :
              result === "wrong"      ? "bg-red-50    text-red-700    border border-red-200"    :
                                        "bg-gray-50   text-gray-500   border border-gray-200"
            }`}>
              {status === "idle"                            && "Select a difficulty and press Start Challenge to begin."}
              {isMemorize                                   && "⏱ Memorise the grid carefully — changes are coming!"}
              {isFind                                       && "🔍 Something changed — click the cell that's different!"}
              {status === "result" && result === "correct"  && `✅ Correct! +${diffConfig.xp} XP${streakBonus ? ` · ${streakBonus}` : ""}`}
              {status === "result" && result === "wrong"    && "❌ Incorrect — the changed cell has been highlighted."}
            </div>

            {/* Timer bar — only during memorise phase */}
            {isMemorize && (
              <TimerBar ms={memMs} active={timerActive} />
            )}

            {/* Round info */}
            {status !== "idle" && (
              <div className="flex justify-between items-center text-xs text-gray-400 mb-3">
                <span>Round {level} · <DifficultyBadge mode={diffMode} /></span>
                <span>Grid: {gridSize}×{gridSize}</span>
              </div>
            )}

            {/* Grid */}
            {status !== "idle" ? (
              <div
                className="grid gap-2 mx-auto mb-4"
                style={{
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  maxWidth: cols <= 4 ? "320px" : cols === 5 ? "380px" : cols === 6 ? "420px" : "480px",
                }}
              >
                {grid.map((cell, idx) => {
                  const isChanged = targetCells.includes(idx);
                  const isFlash   = flashIdx === idx;
                  const isDone    = status === "result";

                  let bg = "bg-gray-50 border-gray-200";
                  if (isFind)                                  bg = "bg-white border-gray-300 cursor-pointer hover:border-amber-400 hover:bg-amber-50 active:scale-95";
                  if (isFlash && flashType === "correct")      bg = "bg-green-100 border-green-400 scale-95";
                  if (isFlash && flashType === "wrong")        bg = "bg-red-100   border-red-400   scale-95";
                  if (isDone && isChanged && result === "correct") bg = "bg-green-50 border-green-400";
                  if (isDone && isChanged && result === "wrong")   bg = "bg-amber-50 border-amber-400 animate-pulse";

                  const orig = originalCells[idx];

                  return (
                    <button
                      key={idx}
                      onClick={() => handleCellClick(idx)}
                      disabled={!isFind}
                      className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center transition-all select-none relative ${bg}`}
                    >
                      <span
                        className={`${cell.color} font-bold`}
                        style={{ fontSize: cols <= 4 ? "1.5rem" : cols === 5 ? "1.25rem" : "1rem" }}
                      >
                        {cell.symbol}
                      </span>
                      {/* Show what changed after result */}
                      {isDone && isChanged && orig && (
                        <span className="absolute bottom-0.5 text-center leading-none" style={{ fontSize: "8px" }}>
                          <span className={`${orig.color} opacity-60`}>{orig.symbol}</span>
                          <span className="text-gray-300 mx-0.5">→</span>
                          <span className={cell.color}>{cell.symbol}</span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2 max-w-xs mx-auto mb-4">
                {Array.from({ length: 16 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-xl border-2 border-dashed border-gray-200 bg-gray-50" />
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              {status === "idle" && (
                <button
                  onClick={startRound}
                  className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors text-sm shadow-sm"
                >
                  Start Challenge
                </button>
              )}
              {status === "result" && (
                <button
                  onClick={startRound}
                  className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors text-sm shadow-sm"
                >
                  Next Round →
                </button>
              )}
              {(isMemorize || isFind) && (
                <button
                  disabled
                  className="flex-1 py-3 bg-gray-100 text-gray-400 font-bold rounded-xl text-sm cursor-not-allowed"
                >
                  {isMemorize ? "Memorising…" : "Find the change!"}
                </button>
              )}
              {status !== "idle" && (
                <button
                  onClick={resetGame}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold rounded-xl transition-colors text-sm"
                >
                  Quit
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Personal Bests footer ── */}
        {status === "idle" && ((best.score ?? 0) > 0 || (best.xp ?? 0) > 0) && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Your Personal Bests</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-600">
              <span>🏆 Score: <b className="text-gray-800">{best.score ?? 0}</b></span>
              <span>🎯 Accuracy: <b className="text-gray-800">{best.accuracy ?? 0}%</b></span>
              <span>⚡ Streak: <b className="text-gray-800">{best.streak ?? 0}</b></span>
              <span>🌟 XP: <b className="text-gray-800">{best.xp ?? 0}</b></span>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-300 pb-2">
          BharatPariksha · Observation Challenge · Build your attention skills
        </p>
      </div>
    </div>
  );
}